import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import CircularProgress from "@mui/material/CircularProgress";

import HistogramColumnChart from "../../shared/charts/HistogramColumnChart";
import { InsulationType, MeasurementType } from "../../shared/enums/Result";
import { UTP2EventHeadings } from "../../shared/enums/UTP2";
import { Instrument, RAGThresholds } from "../../shared/interfaces/Result";
import { UTP2Events } from "../../shared/interfaces/UTP2";

import sortBy from "lodash/sortBy";

interface Props {
  loading?: boolean;
  data?: UTP2Events;
  measurementType: MeasurementType;
  insulation: InsulationType;
  instrument: Instrument;
  uhfBaseline?: number;
  reflow?: number;
}

interface HistogramBin {
  x: number;
  min: number;
  max: number;
  count: number;
}

export function HistogramChart(props: Props) {
  const { t } = useTranslation();
  const [series, setSeries] = useState<any | undefined>();

  const xAxisOptions = getXAxisOptions(props.measurementType, props.uhfBaseline);

  useEffect(() => {
    if (props.measurementType === MeasurementType.CablePD || props.measurementType === MeasurementType.VPIS) {
      const plots =
        props.data &&
        buildHistogramSeriesCablePD(props.data, props.measurementType, props.insulation, props.instrument);
      setSeries(plots);
    } else {
      const plots =
        props.data && buildHistogramSeriesOther(props.data, props.measurementType, props.insulation, props.instrument);
      setSeries(plots);
    }
  }, [props.data, props.measurementType, props.insulation, props.instrument]);

  if (props.loading) {
    return (
      <div className="d-flex justify-content-center">
        <CircularProgress size={65} />
      </div>
    );
  } else if (series) {
    return (
      <HistogramColumnChart
        series={series}
        xAxisTitle={t(xAxisOptions.unitTranslationKey)}
        yAxisTitle={t("units.count")}
        xAxisSettings={xAxisOptions}
        reflow={props.reflow}
      />
    );
  } else {
    return null;
  }
}

function getXAxisOptions(measurementType: MeasurementType, uhfBaseline?: number) {
  if (measurementType === MeasurementType.UHF)
    return {
      unitTranslationKey: "units.dbm",

      min: uhfBaseline || 0,
      max: (uhfBaseline || 0) + 60,
    };
  else if (measurementType === MeasurementType.TEV)
    return {
      unitTranslationKey: "units.db",
      min: 0,
      max: 60,
    };
  else if (measurementType === MeasurementType.CablePD)
    return {
      unitTranslationKey: "units.pc",
      min: 0,
      max: 100,
      tickInterval: 20,
      labels: {
        //convert tickmark labels to exponents
        formatter: function (tick: Highcharts.AxisLabelsFormatterContextObject) {
          const exponent = (tick.value as number) / 10 / 2;
          const label = Math.pow(10, exponent);
          return label.toString();
        },
      },
    };
  else if (measurementType === MeasurementType.VPIS)
    return {
      unitTranslationKey: "units.mV",
      min: 0,
      max: 100,
      tickInterval: 20,
      labels: {
        //convert tickmark labels to exponents
        formatter: function (tick: Highcharts.AxisLabelsFormatterContextObject) {
          const exponent = (tick.value as number) / 10 / 2;
          const label = Math.pow(10, exponent);
          return label.toString();
        },
      },
    };
  else
    return {
      unitTranslationKey: "",
      min: 0,
      max: 60,
    };
}

function getColourByThreshold(value: number, ragThresholds: RAGThresholds) {
  var colours = { red: "#CC241D", amber: "#FFA500", green: "#008000" };

  if (value >= ragThresholds.Red) {
    return colours.red;
  } else if (value >= ragThresholds.Amber) {
    return colours.amber;
  } else {
    return colours.green;
  }
}

function getHeadingIndex(headings: string[], measurementType: MeasurementType) {
  if (measurementType === MeasurementType.CablePD)
    return headings.findIndex((heading) => heading === UTP2EventHeadings.Integral);
  else return headings.findIndex((heading) => heading === UTP2EventHeadings.PeakAmplitude);
}

function getBaseLog(x: number, y: number) {
  return Math.log(y) / Math.log(x);
}

function buildHistogramSeriesCablePD(
  events: UTP2Events,
  measurementType: MeasurementType,
  insulation: InsulationType,
  instrument: Instrument,
) {
  const headingIndex = getHeadingIndex(events.headings, measurementType);

  const histogramBins: HistogramBin[] = events?.data.reduce((accumulator: HistogramBin[], dataValue) => {
    const baseValue = Math.abs(dataValue[headingIndex]);

    let currentValue = Math.round(20.0 * getBaseLog(10, baseValue));
    currentValue = currentValue > 100 ? 100 : currentValue;

    const foundValue = accumulator.find((record) => record.x === currentValue);

    if (!foundValue) {
      return (accumulator = [...accumulator, { x: currentValue, min: baseValue, max: baseValue, count: 1 }]);
    } else {
      if (baseValue < foundValue.min) {
        foundValue.min = baseValue;
      }
      if (baseValue > foundValue.max) {
        foundValue.max = baseValue;
      }

      foundValue.count++;

      return accumulator;
    }
  }, []);

  const thresholds: RAGThresholds | undefined =
    insulation === InsulationType.PILC
      ? instrument["Cable PD PILC thresholds"]
      : instrument["Cable PD XLPE thresholds"];

  const seriesData = sortBy(histogramBins, "x").map((bin) => {
    let colour = thresholds && getColourByThreshold(bin.x, thresholds);

    return {
      name: `${bin.min} - ${bin.max}`,
      x: bin.x,
      y: bin.count,
      color: colour,
    };
  });

  return [
    {
      data: seriesData,
    },
  ];
}

function buildHistogramSeriesOther(
  events: UTP2Events,
  measurementType: MeasurementType,
  insulation: InsulationType,
  instrument: Instrument,
) {
  if (events.headings == null) return [];
  const headingIndex = getHeadingIndex(events.headings, measurementType);

  const histogramBins: HistogramBin[] = events?.data.reduce((accumulator: HistogramBin[], dataValue) => {
    let currentValue = Math.round(dataValue[headingIndex]);

    const foundValue = accumulator.find((record) => record.x === currentValue);

    if (!foundValue) {
      return (accumulator = [...accumulator, { x: currentValue, min: 0, max: 0, count: 1 }]);
    } else {
      foundValue.count++;
      return accumulator;
    }
  }, []);

  let thresholds: RAGThresholds = instrument["TEV Thresholds"];

  let seriesData = sortBy(histogramBins, "x").map((bin) => {
    var colour = thresholds && getColourByThreshold(bin.x, thresholds);

    return {
      name: `${bin.x}`,
      x: bin.x,
      y: bin.count,
      color: colour,
    };
  });

  return [
    {
      data: seriesData,
    },
  ];
}
